import { Dropdown, Nav } from "react-bootstrap";

import type { DashboardListItem } from "src/types/dashboard";

interface DashboardNavDropdownProps {
  dropdownKey: string;
  label: string;
  dashboards: DashboardListItem[];
  currentDashboardCode: string;
  openKey: string | null;
  onOpen: (key: string) => void;
  onScheduleClose: (key: string) => void;
  onSetOpenKey: (key: string | null) => void;
  onNavigate: (code: string) => void;
  onSetDefault: (code: string) => void;
  /** Shown instead of an empty menu when `dashboards` is empty. Only "My
   *  Dashboards" passes this — the per-section dropdowns never rendered an
   *  empty-state message before this was extracted, so leaving it unset
   *  there just renders an empty menu, same as before. */
  emptyMessage?: string;
}

/**
 * One nav dropdown listing dashboards, with a star toggle to set the
 * default. Shared by the per-section dropdowns and "My Dashboards", which
 * were near-identical duplicated JSX before this extraction.
 */
export default function DashboardNavDropdown({
  dropdownKey,
  label,
  dashboards,
  currentDashboardCode,
  openKey,
  onOpen,
  onScheduleClose,
  onSetOpenKey,
  onNavigate,
  onSetDefault,
  emptyMessage,
}: DashboardNavDropdownProps) {
  const hasActiveDashboard = dashboards.some(
    (dashboard) => currentDashboardCode === dashboard.code,
  );

  return (
    <Dropdown
      as="li"
      className="nav-item"
      show={openKey === dropdownKey}
      onMouseEnter={() => onOpen(dropdownKey)}
      onMouseLeave={() => onScheduleClose(dropdownKey)}
      onToggle={(nextShow) => onSetOpenKey(nextShow ? dropdownKey : null)}
    >
      <Dropdown.Toggle
        as={Nav.Link}
        id={dropdownKey}
        className={`${hasActiveDashboard ? " custom-active-link" : ""}`}
      >
        {label}
      </Dropdown.Toggle>
      <Dropdown.Menu
        renderOnMount
        onMouseEnter={() => onOpen(dropdownKey)}
        onMouseLeave={() => onScheduleClose(dropdownKey)}
        popperConfig={{
          modifiers: [
            {
              name: "offset",
              options: { offset: [0, 12] },
            },
          ],
        }}
      >
        {dashboards.length > 0
          ? dashboards.map((dashboard) => (
              <Dropdown.Item
                key={dashboard.code}
                as="button"
                active={currentDashboardCode === dashboard.code}
                className="d-flex align-items-center justify-content-between"
                onClick={() => onNavigate(dashboard.code)}
              >
                {dashboard.name}
                <i
                  className={`bi ms-2 ${dashboard.is_default ? "bi-star-fill text-warning" : "bi-star"}`}
                  role="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!dashboard.is_default) onSetDefault(dashboard.code);
                  }}
                />
              </Dropdown.Item>
            ))
          : emptyMessage && (
              <Dropdown.Item disabled>{emptyMessage}</Dropdown.Item>
            )}
      </Dropdown.Menu>
    </Dropdown>
  );
}
